// @ts-check
/* ═══════════════════════ VALUTAKURSER (delt) ═══════════════════════
   Henter CAD/USD/NOK fra /api/rates og cacher i localStorage med
   tidsstempel. Når appen er offline (eller API-et svarer 503), faller
   vi tilbake til siste kjente kurs og flagger hvor gammel den er — i
   stedet for å vise en blank linje eller spinner (#4 graceful degradation).
   ═══════════════════════════════════════════════════════════════════ */

import { fetchWithTimeout } from './utils.js';

const LS_KEY = 'ca26rates_v1';
const FALLBACK = { 'CAD-NOK': 7.65, 'CAD-USD': 0.71, 'USD-NOK': 10.75, 'USD-CAD': 1.41 };
const FRESH_MS = 6 * 60 * 60 * 1000; // 6 t regnes som ferskt

let mem = null;

function read(){
  if (mem) return mem;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) mem = JSON.parse(raw);
  } catch {}
  return mem;
}

function write(rates){
  mem = { rates, ts: Date.now(), updated: rates.updated || null };
  try { localStorage.setItem(LS_KEY, JSON.stringify(mem)); } catch {}
}

/**
 * Returnerer { rates, ageMs, stale, fromCache, updated }.
 * Henter ferskt fra nett hvis cachen er gammel og vi er online,
 * ellers serveres cachet/fallback umiddelbart.
 */
export async function getRates(){
  const cached = read();
  const ageMs = cached ? Date.now() - cached.ts : Infinity;

  // Frisk nok? Returner med en gang.
  if (cached && ageMs < FRESH_MS) {
    return { rates: cached.rates, ageMs, stale: false, fromCache: true, updated: cached.updated };
  }

  // Prøv nett — men bare hvis vi tror vi er online.
  if (navigator.onLine) {
    try {
      const r = await fetchWithTimeout('/api/rates');
      if (r.ok) {
        const json = await r.json();
        if (json && json['CAD-NOK']) {
          write(json);
          return { rates: json, ageMs: 0, stale: false, fromCache: false, updated: json.updated };
        }
      }
    } catch {}
  }

  // Offline eller feilet — bruk det vi har.
  if (cached) {
    return { rates: cached.rates, ageMs, stale: true, fromCache: true, updated: cached.updated };
  }
  return { rates: FALLBACK, ageMs: Infinity, stale: true, fromCache: false, updated: null };
}

/** Synkron tilgang til siste kjente kurs (eller fallback) — for render uten await. */
export function peekRates(){
  const cached = read();
  return cached?.rates || FALLBACK;
}

/** Menneskelig «for X siden»-tekst for staleness-merking. */
export function ageLabel(ageMs){
  if (!isFinite(ageMs)) return 'ukjent alder';
  const min = Math.round(ageMs / 60000);
  if (min < 1) return 'nå nettopp';
  if (min < 60) return `${min} min siden`;
  const hrs = Math.round(min / 60);
  if (hrs < 24) return `${hrs} t siden`;
  return `${Math.round(hrs / 24)} d siden`;
}
